// ✅ src/pages/Asset/VehicleCalibrationAlertReport.jsx  (FULL FILE)
// UPDATED: Added your "Nissi Infotech" print header block on top.
//
// Backend:
//   GET /api/assetHome/calibration-alerts
// Optional meta props are supported (fromdate/todate/branch/project etc.)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaPrint, FaArrowLeft, FaRedo } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/assetHome";
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const readQS = (search) => {
  const sp = new URLSearchParams(search || "");
  const obj = {};
  for (const [k, v] of sp.entries()) obj[k] = v;
  return obj;
};

export default function VehicleCalibrationAlertReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const qs = useMemo(() => readQS(location.search), [location.search]);

  // Optional header meta (same style you used in other print pages)
  const title = qs.title || "VEHICLE CALIBRATION ALERT REPORT";
  const fromDate = qs.from || qs.fromDate || "";
  const toDate = qs.to || qs.toDate || "";
  const branchLabel = qs.branchLabel || "";
  const projectLabel = qs.projectLabel || "";
  const mode = qs.mode || "";
  const printId = qs.id || "0"; // "1" => All, else Abstract

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API_BASE}/calibration-alerts`);
      if (!res.data?.success) {
        setErr(res.data?.message || "Failed to load report data");
        setRows([]);
      } else {
        setRows(res.data?.data || []);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Server error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const expireToday = rows.filter((x) => toStr(x.Status).toLowerCase().includes("today")).length;
    return { total, expireToday };
  }, [rows]);

  const statusClass = (status) => {
    const s = toStr(status).toLowerCase();
    if (s.includes("today") || s.includes("expired")) return "text-red-700 font-semibold";
    if (s.includes("day left") || s.includes("days left")) return "text-yellow-700 font-semibold";
    return "text-green-700 font-semibold";
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Toolbar - hidden on print */}
      <div className="sticky top-0 z-50 bg-white border-b print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm font-semibold"
              title="Back"
            >
              <FaArrowLeft className="inline mr-2" />
              Back
            </button>

            <button
              onClick={load}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm font-semibold"
              title="Refresh"
              disabled={loading}
            >
              <FaRedo className="inline mr-2" />
              Refresh
            </button>
          </div>

          <button
            onClick={() => window.print()}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black text-sm font-semibold"
            title="Print"
          >
            <FaPrint className="inline mr-2" />
            Print
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ✅ NEW HEADER YOU ASKED (added) */}
        <div className="border-2 border-black mb-4">
          <div className="text-center py-3 border-b border-black">
            <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
            <div className="text-sm">Nissi</div>
            <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
            <div className="text-xs">
              3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,India
            </div>
            <div className="text-xs">
              Phone No : 9003744644 Email : infraconsoft-implementation@nissiinfotech.in
            </div>
          </div>

          <div className="text-center py-2 border-b border-black font-bold">
            {title}
          </div>

          {/* ✅ Filter Header */}
          <div className="px-3 py-2 border-b border-black text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <b>From :</b> {fromDate || "-"}
              </div>
              <div>
                <b>To :</b> {toDate || "-"}
              </div>
              <div>
                <b>Branch :</b> {branchLabel || "-"}
              </div>
              <div>
                <b>Project :</b> {projectLabel || "-"}
              </div>
            </div>

            <div className="mt-1 text-xs text-gray-700">
              <b>View :</b> {printId === "1" ? "All" : "Abstract"} &nbsp; | &nbsp;
              <b>Mode :</b> {mode || "-"}
            </div>
          </div>
        </div>

        {/* Small summary line (optional) */}
        <div className="flex items-center justify-between mb-3 text-sm text-gray-700">
          <div>
            <b>Total:</b> {stats.total} &nbsp; | &nbsp; <b>Expire Today:</b>{" "}
            <span className="text-red-700 font-semibold">{stats.expireToday}</span>
          </div>
          <div>
            <b>Generated:</b> {new Date().toLocaleString()}
          </div>
        </div>

        {/* Errors / Loading */}
        {err ? (
          <div className="border border-red-200 bg-red-50 text-red-700 p-4 rounded-xl mb-6">
            {err}
          </div>
        ) : null}

        {loading ? (
          <div className="p-8 text-center text-gray-600 border rounded-xl">Loading report...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-600 border rounded-xl">No Records Found</div>
        ) : (
          <div className="border border-black">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-black px-3 py-2 text-left">S.No</th>
                  <th className="border border-black px-3 py-2 text-left">Asset Name</th>
                  <th className="border border-black px-3 py-2 text-left">Asset Code</th>
                  <th className="border border-black px-3 py-2 text-left">Vehicle Reg.No</th>
                  <th className="border border-black px-3 py-2 text-left">Next Calibration Date</th>
                  <th className="border border-black px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.fld_assetid}-${idx}`}>
                    <td className="border border-black px-3 py-2">{idx + 1}</td>
                    <td className="border border-black px-3 py-2">{toStr(r.fld_assettypename)}</td>
                    <td className="border border-black px-3 py-2">{toStr(r.fld_assetcode)}</td>
                    <td className="border border-black px-3 py-2">{toStr(r.fld_description)}</td>
                    <td className="border border-black px-3 py-2">{toStr(r.fld_nextcalibrationdate)}</td>
                    <td className={`border border-black px-3 py-2 ${statusClass(r.Status)}`}>
                      {toStr(r.Status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-600">
          * Report Source: usp_Asset_alertforcalibrationrenewal
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          table { page-break-inside: auto; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
        }
      `}</style>
    </div>
  );
}
